if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = { toggleZoom: [".js-photoswipe--zoom"] };
        this.visibleMediaIds = null;
        this.reconciliationQueued = false;
        this.handlePageShow = this.handlePageShow.bind(this);
      }

      connectedCallback() {
        window.addEventListener("pageshow", this.handlePageShow);
        if (this.initialized) {
          this.swiper?.connect();
          if (this.enableZoom && !this.lightbox) this.initImageZoom();
          this.refresh();
          return;
        }
        this.initialized = true;
        this.init();
      }

      disconnectedCallback() {
        window.removeEventListener("pageshow", this.handlePageShow);
        this.swiper?.destroy();
        this.destroyImageZoom();
      }

      handlePageShow(event) {
        if (!event.persisted) return;
        this.refresh();
        if (this.enableZoom && !this.lightbox) this.initImageZoom();
      }

      getSelectedVariant() {
        const selectedVariant = this.querySelector(
          "[data-selected-variant]",
        )?.textContent;
        return selectedVariant?.trim() ? JSON.parse(selectedVariant) : null;
      }

      getVariantData() {
        if (this.variantData) return this.variantData;
        const script = this.querySelector(
          'script[type="application/json"]:not([data-selected-variant])',
        );
        this.variantData = JSON.parse(script.textContent);
        return this.variantData;
      }

      ensureSwiper() {
        if (
          !this.swiper &&
          typeof window.ProductMediaSwiper === "function"
        ) {
          this.swiper = new window.ProductMediaSwiper(this);
        }
        if (this.isConnected) this.swiper?.connect();
        return this.swiper;
      }

      init() {
        const mediaGallery = this.querySelector('[id^="Media-Gallery"]');
        const mediaWrapper = mediaGallery?.querySelector(
          ":scope > .swiper-wrapper",
        );
        const thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
        this.domNodes = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          mediaGallery,
          medias: [
            ...(mediaWrapper?.querySelectorAll(":scope > .f-product__media") ||
              []),
          ],
          sliderCounter: this.querySelector("[data-media-counter-current]"),
          sliderCounterTotal: this.querySelector("[data-media-counter-total]"),
          thumbnails,
          thumbnailItems: [
            ...(thumbnails?.querySelectorAll(
              ".f-product__media-thumbnails-item",
            ) || []),
          ],
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          xrButton: this.querySelector("[data-first-xr-button]"),
        };
        this.mediaWrapper = mediaWrapper;
        this.allMedias = [...this.domNodes.medias];
        this.allThumbnails = [...this.domNodes.thumbnailItems];
        this.mediaById = new Map(
          this.allMedias.map((media) => [media.dataset.mediaId, media]),
        );
        this.thumbnailById = new Map(
          this.allThumbnails.map((thumbnail) => [
            thumbnail.dataset.mediaId,
            thumbnail,
          ]),
        );
        this.mediaLayout = mediaGallery?.dataset.mediaLayout || "carousel";
        this.onlyImage = this.dataset.onlyImage === "true";
        this.enableZoom = this.dataset.enableZoom === "true";
        this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true";
        this.section = this.closest("product-info");
        const variantPicker = this.section?.querySelector(
          "[data-variant-picker]",
        );
        this.disableDefaultVariant =
          variantPicker?.dataset.disableDefaultVariant === "true";
        const selectedVariantId = this.section?.querySelector(
          'input[name="id"]',
        )?.value;
        const variantData = this.getVariantData();
        this.currentVariant = selectedVariantId
          ? variantData.find(
              (variant) => variant.id === Number(selectedVariantId),
            )
          : this.getSelectedVariant();
        this.selectedMediaId = this.allMedias[0]?.dataset.mediaId || null;

        this.swiper = null;
        this.ensureSwiper()?.refresh();

        const featuredMediaId = this.currentVariant?.featured_media?.id;
        if (
          featuredMediaId &&
          !this.disableDefaultVariant &&
          !this.showFeaturedMedia
        ) {
          this.selectMedia(featuredMediaId, { instant: true });
        } else if (this.selectedMediaId) {
          this.selectMedia(this.selectedMediaId, { instant: true });
        }

        if (this.enableZoom && window.FoxTheme?.PhotoSwipeLightbox) {
          this.initImageZoom();
        }
        this.removeAttribute("data-media-loading");
        this.dataset.mediaReady = "true";
        queueMicrotask(() => {
          this.dispatchEvent(
            new CustomEvent("product-media:loaded", {
              bubbles: true,
              detail: { currentVariant: this.currentVariant },
            }),
          );
        });
      }

      setVisibleMedia(mediaIds, { selectedMediaId = null } = {}) {
        this.visibleMediaIds = mediaIds === null ? null : mediaIds.map(String);
        if (selectedMediaId !== null) {
          this.requestedMediaId = String(selectedMediaId);
        }
        if (this.reconciliationQueued) return;
        this.reconciliationQueued = true;
        queueMicrotask(() => this.reconcileVisibleMedia());
      }

      reconcileVisibleMedia() {
        this.reconciliationQueued = false;
        const canonicalIds = this.allMedias.map((media) => media.dataset.mediaId);
        let ids = this.visibleMediaIds
          ? [...new Set(this.visibleMediaIds)].filter((id) => this.mediaById.has(id))
          : canonicalIds;
        if (!ids.length && this.allMedias[0]) {
          ids = [this.allMedias[0].dataset.mediaId];
          console.warn("[media-gallery] Visible media set was empty");
        }

        this.allMedias.forEach((media) => media.remove());
        this.allThumbnails.forEach((thumbnail) => thumbnail.remove());
        ids.forEach((id, index) => {
          const media = this.mediaById.get(id);
          if (!media) return;
          media.dataset.mediaIndex = String(index);
          media.dataset.index = String(index);
          media
            .querySelector(".js-photoswipe--zoom")
            ?.setAttribute("data-media-index", String(index));
          this.mediaWrapper?.append(media);
          const thumbnail = this.thumbnailById.get(id);
          if (thumbnail) {
            thumbnail.dataset.thumbnailIndex = String(index);
            this.domNodes.thumbnails
              ?.querySelector(":scope > .swiper-wrapper")
              ?.append(thumbnail);
          }
        });
        this.domNodes.medias = ids
          .map((id) => this.mediaById.get(id))
          .filter(Boolean);
        this.domNodes.thumbnailItems = ids
          .map((id) => this.thumbnailById.get(id))
          .filter(Boolean);
        if (this.domNodes.sliderCounterTotal) {
          this.domNodes.sliderCounterTotal.textContent = String(ids.length);
        }
        let selectedId = ids[0];
        if (ids.includes(this.requestedMediaId)) {
          selectedId = this.requestedMediaId;
        }
        if (ids.includes(this.selectedMediaId)) {
          selectedId = this.selectedMediaId;
        }
        this.swiper?.refresh();
        this.selectMedia(selectedId, { instant: true });
        this.refreshImageZoom();
      }

      selectMedia(mediaId, { instant = false, focus = false } = {}) {
        const id = String(mediaId);
        const media = this.mediaById.get(id);
        if (!media || !this.domNodes.medias.includes(media)) return false;
        this.ensureSwiper()?.selectMedia(id, { instant });
        this.handleMediaChange(id);
        if (focus) media.focus({ preventScroll: true });
        if (!this.swiper?.main && this.mediaLayout !== "carousel") {
          media.scrollIntoView({ behavior: instant ? "auto" : "smooth" });
        }
        return true;
      }

      refresh({ preserveSelection = true } = {}) {
        const selectedId = preserveSelection ? this.selectedMediaId : null;
        this.ensureSwiper()?.refresh();
        if (selectedId) this.selectMedia(selectedId, { instant: true });
      }

      handleMediaChange(mediaId) {
        if (!mediaId) return;
        const id = String(mediaId);
        const selected = this.mediaById.get(id);
        if (!selected) return;
        this.selectedMediaId = id;
        this.domNodes.medias.forEach((media, index) => {
          const isSelected = media === selected;
          media.classList.toggle("is-selected", isSelected);
          if (this.swiper?.main) {
            media.setAttribute("aria-hidden", String(!isSelected));
            media.toggleAttribute("inert", !isSelected);
          } else {
            media.removeAttribute("aria-hidden");
            media.removeAttribute("inert");
          }
          if (isSelected) media.dataset.mediaIndex = String(index);
        });
        this.domNodes.viewer?.style.setProperty(
          "--media-aspect-ratio",
          selected.dataset.aspectRatio || "1",
        );
        if (this.domNodes.sliderCounter) {
          this.domNodes.sliderCounter.textContent = String(
            this.domNodes.medias.indexOf(selected) + 1,
          );
        }
        this.domNodes.thumbnailItems.forEach((thumbnail) => {
          const isSelected = thumbnail.dataset.mediaId === id;
          thumbnail.classList.toggle("is-selected", isSelected);
          if (isSelected) {
            thumbnail.setAttribute("aria-current", "true");
          } else {
            thumbnail.removeAttribute("aria-current");
          }
        });
        if (selected.dataset.mediaType === "model") {
          this.domNodes.xrButton?.setAttribute("data-shopify-model3d-id", id);
        }
        if (this.domNodes.liveRegion) {
          const position = this.domNodes.medias.indexOf(selected) + 1;
          const mediaType = (selected.dataset.mediaType || "media").replace(
            "_",
            " ",
          );
          this.domNodes.liveRegion.textContent = `${mediaType} ${position} of ${this.domNodes.medias.length}`;
        }
        this.playActiveMedia(selected);
      }

      destroyImageZoom() {
        this.removeZoomDelegate && this.removeZoomDelegate();
        this.removeZoomDelegate = null;
        this.lightbox && this.lightbox.destroy();
        this.lightbox = null;
      }

      refreshImageZoom() {
        if (!this.enableZoom) return;
        this.destroyImageZoom();
        this.initImageZoom();
      }

      initImageZoom() {
        const dataSource = this.domNodes.medias
          .map((media) => {
            const mediaType = media.dataset.mediaType;
            const id = media.dataset.mediaId;
            if (mediaType === "image") {
              return {
                id,
                src: media.dataset.src,
                width: media.dataset.pswpWidth,
                height: media.dataset.pswpHeight,
                mediaType,
              };
            }
            const embeddedMedia = media.querySelector(
              mediaType === "model" ? "product-model" : "deferred-media",
            );
            if (!embeddedMedia) return null;
            return {
              id,
              html: `<div class="pswp__item--${mediaType}">${embeddedMedia.outerHTML}</div>`,
              mediaType,
            };
          })
          .filter(Boolean);

        const options = {
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        };

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, data, index) => {
          const el = this.querySelector(
            '[data-media-id="' + data.id + '"] img',
          );
          if (el) {
            return el;
          }
          return thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, slide) => {
          const el = this.querySelector(
            '[data-media-id="' + slide.data.id + '"] img',
          );
          if (el) {
            return el.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on("change", () => {
          const mediaId = this.lightbox.pswp.currSlide?.data?.id;
          if (mediaId) this.selectMedia(mediaId, { instant: true });
        });

        this.lightbox.on("pointerDown", (e) => {
          const currSlide = this.lightbox.pswp.currSlide.data;
          if (
            currSlide.mediaType === "video" ||
            currSlide.mediaType === "model" ||
            currSlide.mediaType === "external_video"
          ) {
            e.preventDefault();
          }
        });

        this.lightbox.on("uiRegister", () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: "next",
              ariaLabel: "Next slide",
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: "prev",
              ariaLabel: "Previous slide",
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev f-rlt-reverse-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z" class="arrow"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }

          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="f-icon-svg f-icon--medium  f-icon-close"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        this.removeZoomDelegate && this.removeZoomDelegate();
        this.removeZoomDelegate = addEventDelegate({
          selector: this.selectors.toggleZoom[0],
          context: this,
          handler: (e, media) => {
            const mediaContainer = media.closest(".f-product__media");
            const mediaType = mediaContainer
              ? mediaContainer.dataset.mediaType
              : media.dataset.mediaType;
            const mediaId = (mediaContainer || media).dataset.mediaId;

            if (FoxThemeSettings.isMobile) {
              if (mediaType === "video" || mediaType === "external_video") {
                e.preventDefault();
                return false;
              }

              if (mediaId !== this.selectedMediaId) {
                e.preventDefault();
                return false;
              }
            }

            if (mediaType !== "image") return;

            const slideIndex = dataSource.findIndex(
              (item) => item.id === mediaId,
            );
            if (slideIndex === -1) return;

            this.lightbox.loadAndOpen(slideIndex);
          },
        });
      }

      playActiveMedia(selected) {
        window.pauseAllMedia();
        const deferredMedia = selected.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }
    }
  );
}
